/**
 * El cupón de un grupo con descuento, en Mensajes (fase 4): uno del catálogo de Cupones que diga exactamente lo que
 * promete el mensaje (el mismo % sobre el pedido, la misma compra mínima, activo y sin vencer), que no lleve otro
 * grupo. Si no hay ninguno, «Crear uno con esta oferta» lo genera (palabra + 4 al azar) y lo guarda en Cupones: el
 * equipo lo tiene que cargar igual en el sistema de pedidos de la marca.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Plus } from "lucide-react";
import { num } from "../../campana-nueva/utils/format";
import { cuponesApi } from "../../cupones/api";
import { R_CUPONES } from "../../cupones/logic/cupones";
import type { Cupon } from "../../cupones/types";
import { llamar } from "../../wa-oficial/components/base/comun";
import { textoError } from "../../wa-oficial/logic/respuesta";
import { cuponesParaElegir, textoVigencia } from "../logic/mensajes";
import type { CuponDeLaVista, Mensaje } from "../types";

const ayuda = "text-sm text-slate-600";

export function CuponDelGrupo({
  grupoId,
  b,
  cupones,
  elegidos,
  vigenciaDias,
  alElegir,
  alCrear,
}: {
  grupoId: string;
  b: Pick<Mensaje, "oferta" | "minimo" | "cuponId">;
  cupones: CuponDeLaVista[];
  /** cuponId → grupo, de lo elegido en la pantalla (aunque no se haya guardado) */
  elegidos: ReadonlyMap<string, string>;
  vigenciaDias: number;
  alElegir: (cuponId: string | null) => void;
  alCrear: (c: Cupon) => void;
}) {
  const [creando, setCreando] = useState(false);
  const [error, setError] = useState("");
  const [recien, setRecien] = useState("");
  const opciones = cuponesParaElegir(cupones, grupoId, b, elegidos);
  const elegido = b.cuponId ? cupones.find((c) => c.id === b.cuponId) : undefined;
  // el elegido se sigue viendo aunque ya no sirva (con su aviso): así se sabe cuál era
  const lista = elegido && !opciones.some((c) => c.id === elegido.id) ? [elegido, ...opciones] : opciones;
  const queBusca = `${b.oferta}% del pedido${b.minimo === null ? " sin mínimo" : ` con compra mínima de $${num(b.minimo)}`}`;

  const crear = async () => {
    setCreando(true);
    setError("");
    try {
      const g = await llamar<{ codigo: string }>(() => cuponesApi.generar(""));
      if (!g.ok) return setError(textoError(g));
      const l = await llamar<Cupon>(() =>
        cuponesApi.crear({
          codigo: g.data.codigo,
          descuentoPct: b.oferta,
          aplicaA: "pedido",
          compraMinima: b.minimo,
          vigencia: { tipo: "dias", dias: vigenciaDias },
          noIncluye: "",
          generado: true,
        }),
      );
      if (!l.ok) return setError(textoError(l));
      setRecien(l.data.codigo);
      alCrear(l.data);
    } finally {
      setCreando(false);
    }
  };

  return (
    <div className="space-y-2 sm:col-span-2">
      <label className="block space-y-2">
        <span className="block text-sm font-semibold text-slate-900">Cupón</span>
        <select
          value={b.cuponId ?? ""}
          onChange={(e) => alElegir(e.target.value || null)}
          className="min-h-[44px] w-full rounded-xl border border-slate-200 bg-white px-3 font-mono text-sm font-semibold text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
        >
          <option value="">{lista.length ? "Elegí el cupón del grupo…" : "No hay ninguno para elegir"}</option>
          {lista.map((c) => (
            <option key={c.id} value={c.id}>
              {c.codigo} · {textoVigencia(c)}
              {/* el elegido que ya no sirve lo dice en la lista, no sólo en el aviso de abajo */}
              {!opciones.some((o) => o.id === c.id) && ` · ${c.estado === "archivado" ? "archivado" : c.vencido ? "vencido" : "no sirve para este grupo"}`}
            </option>
          ))}
        </select>
      </label>
      {!lista.length && <p className={ayuda}>No tenés un cupón de {queBusca} libre en Cupones.</p>}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <button
          type="button"
          disabled={creando}
          onClick={() => void crear()}
          className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-60"
        >
          {creando ? <Loader2 size={15} className="animate-spin" aria-hidden="true" /> : <Plus size={15} aria-hidden="true" />}
          Crear uno con esta oferta
        </button>
        <Link to={R_CUPONES} className="text-sm font-semibold text-violet-700 underline">
          Ver Cupones
        </Link>
      </div>
      {recien && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900" role="status" data-testid="cupon-creado">
          Se creó {recien}: cargalo en el sistema de pedidos de la marca con el {b.oferta}% y la misma vigencia, si no, el cliente no lo puede usar.
        </p>
      )}
      {error && (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      )}
    </div>
  );
}
